// src/components/ResidentSidebar.js - Updated version with responsive language switcher
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import LogoPolderzicht from '../assets/logo_polderzicht_kantoorlaag.png';
import '../css/ResidentSidebar.css';
import '../css/PageLayout.css';

const ResidentSidebar = ({ language, setLanguage }) => {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = !!localStorage.getItem('token');

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle language change
  const handleLanguageChange = (lang) => {
    if (setLanguage) {
      setLanguage(lang);
      localStorage.setItem('language', lang);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  // Handle logout (only relevant if user is logged in)
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('loginTimestamp');
    // Redirect to home page, not login
    navigate('/home');
  };

  // Function to handle external links
  const openExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Create translations
  const translations = {
    nl: {
      home: "Home",
      newResidents: "Nieuwe bewoners",
      regulations: "Huishoudelijk Reglement",
      faq: "Veelgestelde vragen", 
      contact: "Contact",
      calendar: "Kalender",
      admin: "Beheer",
      login: "Inloggen",
      logout: "Uitloggen",
      board: "Bestuur"
    },
    en: {
      home: "Home",
      newResidents: "New Residents",
      regulations: "House Rules",
      faq: "FAQ", 
      contact: "Contact",
      calendar: "Calendar",
      admin: "Admin",
      login: "Login",
      logout: "Logout",
      board: "Board"
    }
  };
  
  // Get current texts based on selected language
  const t = translations[language || 'nl'];

  // SVG icons - Replace emoji icons with SVG icons
  const icons = {
    home: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9 22 9 12 15 12 15 22"></polyline>
      </svg>
    ),
    info: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="16" x2="12" y2="12"></line>
        <line x1="12" y1="8" x2="12.01" y2="8"></line>
      </svg>
    ),
    document: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
    ),
    faq: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
        <line x1="12" y1="17" x2="12.01" y2="17"></line>
      </svg>
    ),
    contact: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
      </svg>
    ),
    admin: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
      </svg>
    ),
    login: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
        <polyline points="10 17 15 12 10 7"></polyline>
        <line x1="15" y1="12" x2="3" y2="12"></line>
      </svg>
    ),
    logout: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
        <polyline points="16 17 21 12 16 7"></polyline>
        <line x1="21" y1="12" x2="9" y2="12"></line>
      </svg>
    ),
    menuOpen: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12"></line>
        <polyline points="12 19 5 12 12 5"></polyline>
      </svg>
    ),
    menuClosed: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="12" x2="21" y2="12"></line>
        <line x1="3" y1="6" x2="21" y2="6"></line>
        <line x1="3" y1="18" x2="21" y2="18"></line>
      </svg>
    )
  };

  return (
    <>
      {/* Sidebar */}
      <aside className={`sidebar resident-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <Link to="/home" className="logo-link">
            <img src={LogoPolderzicht} alt="Polderzicht Logo" className="sidebar-logo" />
            <h2 className="sidebar-title">VVE Polderzicht</h2>
            {/* Language switcher - shown on all screen sizes below the title */}
            <div className="language-switcher">
              <button 
                className={`lang-btn ${language === 'nl' ? 'active' : ''}`} 
                onClick={() => handleLanguageChange('nl')}
              >
                NL
              </button>
              <button 
                className={`lang-btn ${language === 'en' ? 'active' : ''}`} 
                onClick={() => handleLanguageChange('en')}
              >
                EN
              </button>
            </div>
          </Link>
        </div>
        <nav className="sidebar-nav">
          <ul>
            <li>
              <Link to="/home" className={`nav-link ${location.pathname === '/home' ? 'active' : ''}`}>
                <span className="icon">{icons.home}</span>
                <span className="menu-text">{t.home}</span>
              </Link>
            </li>
            <li>
              <Link to="/infonieuw" className={`nav-link ${location.pathname === '/infonieuw' ? 'active' : ''}`}>
                <span className="icon">{icons.info}</span>
                <span className="menu-text">{t.newResidents}</span>
              </Link>
            </li>
            <li>
              <Link to="/hhr" className={`nav-link ${location.pathname === '/hhr' ? 'active' : ''}`}>
                <span className="icon">{icons.document}</span>
                <span className="menu-text">{t.regulations}</span>
              </Link>
            </li>
            <li>
              <Link to="/faq" className={`nav-link ${location.pathname === '/faq' ? 'active' : ''}`}>
                <span className="icon">{icons.faq}</span>
                <span className="menu-text">{t.faq}</span>
              </Link>
            </li>
            {/* Add a Contact section */}
            <li>
              <Link to="/contact" className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}>
                <span className="icon">{icons.contact}</span>
                <span className="menu-text">{t.contact}</span>
              </Link>
            </li>
            
            {/* Board access (login) button - always visible */}
            <li className="board-access-item">
              <Link to="/login" className={`nav-link board-link ${location.pathname === '/login' ? 'active' : ''}`}>
                <span className="icon">{icons.admin}</span>
                <span className="menu-text">{t.login}</span>
              </Link>
            </li>
            
            {/* Logout button - only visible when logged in */}
            {isAuthenticated && (
              <li className="logout-item">
                <button onClick={handleLogout} className="nav-link logout-link">
                  <span className="icon">{icons.logout}</span>
                  <span className="menu-text">{t.logout}</span>
                </button>
              </li>
            )}
          </ul>
        </nav>
        
        {/* Sidebar footer - no language switcher here anymore */}
        <div className="sidebar-footer">
          {/* Footer content can be added here if needed */}
        </div>
      </aside>

      {/* Mobile sidebar toggle - now with SVG icon */}
      <button 
        className="sidebar-toggle" 
        onClick={toggleSidebar}
        aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
      >
        {sidebarOpen ? icons.menuOpen : icons.menuClosed}
      </button>
      
      {/* Mobile backdrop overlay */}
      {sidebarOpen && window.innerWidth <= 768 && 
        <div className="mobile-backdrop" onClick={toggleSidebar}></div>
      }
    </>
  );
};

export default ResidentSidebar;